/**
 * ScrollingPebbleBackground — rising organic pebbles for DENiZEN screens.
 *
 * Renders 16 biomorphic shapes (8 photo pebbles + 8 solid-colour pebbles)
 * that continuously rise upward across the viewport like gentle bubbles.
 * When a pebble exits the top, it recycles to below the viewport. Photo
 * pebbles advance to the next image in their pool on each recycle.
 *
 * Collision detection prevents pebbles from overlapping — on recycle, a
 * pebble's candidate position is checked against all others and pushed
 * further below if needed.
 *
 * Shapes use the SVG clip-paths from PebbleShape (extracted from Figma
 * node 1:48) for an organic look matching the DENiZEN logo motif.
 *
 * Respects prefers-reduced-motion: falls back to a static layout with
 * no animation or image cycling.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { PebbleShape } from './PebbleShape'
import type { PebbleVariant } from './PebbleShape'

export interface GroundingImage {
  image_url: string
  thumbnail_url: string | null
  title?: string | null
}

export interface ScrollingPebbleBackgroundProps {
  images?: GroundingImage[]
  className?: string
}

/* -------------------------------------------------------------------------- */
/*  Pebble slot definitions                                                    */
/* -------------------------------------------------------------------------- */

type PebbleType = 'image' | 'colour'

interface PebbleSlot {
  type: PebbleType
  variant: PebbleVariant
  /** Desktop size in px */
  desktopSize: number
  /** Mobile size in px */
  mobileSize: number
  /** Height/width ratio — defaults to 1 */
  aspectRatio?: number
  /** Horizontal column range [min%, max%] for randomised X placement */
  columnRange: [number, number]
  /** Rise speed in px per frame at 60fps */
  speed: number
  /** Solid colour (colour pebbles only) */
  colour?: string
}

/**
 * 16 pebbles — 8 photo-filled, 8 solid colour.
 * Lanes spread from off-screen edges through to partially behind the
 * stone card, filling the green margins richly. The stone card sits at
 * z-10 above pebbles (z-0) so overlapping pebbles appear behind it.
 *
 * Image and colour pebbles strictly alternate on each side so you never
 * see two or more coloured blobs in a row.
 *
 * Left (8):  img → colour → img → colour → img → colour → img → colour
 * Right (8): img → colour → img → colour → img → colour → img → colour
 *
 * Speeds grouped into two narrow bands to prevent mid-flight overlap:
 *   Image pebbles:  0.18–0.22 px/frame
 *   Colour pebbles: 0.10–0.14 px/frame
 */
const PEBBLE_SLOTS: PebbleSlot[] = [
  // -- Left side (8 pebbles, alternating img/colour) --
  // L1 — large photo blob (bleeds off left edge)
  { type: 'image',  variant: 'blob-1',  desktopSize: 390, mobileSize: 155, aspectRatio: 420 / 472, columnRange: [-15, -5],  speed: 0.22 },
  // L2 — small mid-green accent
  { type: 'colour', variant: 'round-2', desktopSize: 120, mobileSize: 50,  aspectRatio: 268 / 228, columnRange: [-3, 5],    speed: 0.13, colour: '#40aa3b' },
  // L3 — medium photo blob
  { type: 'image',  variant: 'blob-3',  desktopSize: 260, mobileSize: 110, columnRange: [7, 15],    speed: 0.20 },
  // L4 — small teal accent
  { type: 'colour', variant: 'round-1', desktopSize: 90,  mobileSize: 40,  aspectRatio: 257 / 211, columnRange: [17, 25],   speed: 0.11, colour: '#39b0a3' },
  // L5 — small photo blob (partially behind card)
  { type: 'image',  variant: 'blob-2',  desktopSize: 200, mobileSize: 85,  aspectRatio: 462 / 380, columnRange: [27, 34],   speed: 0.18 },
  // L6 — tiny dark green accent
  { type: 'colour', variant: 'round-3', desktopSize: 70,  mobileSize: 30,  columnRange: [2, 8],     speed: 0.10, colour: '#216732' },
  // L7 — small photo blob (far left edge)
  { type: 'image',  variant: 'blob-3',  desktopSize: 160, mobileSize: 65,  columnRange: [-8, 3],    speed: 0.20 },
  // L8 — tiny light green accent (inner left)
  { type: 'colour', variant: 'round-1', desktopSize: 65,  mobileSize: 28,  aspectRatio: 257 / 211, columnRange: [12, 20],   speed: 0.12, colour: '#a5f1ad' },

  // -- Right side (8 pebbles, alternating img/colour) --
  // R1 — medium photo blob (partially behind card)
  { type: 'image',  variant: 'blob-1',  desktopSize: 240, mobileSize: 100, aspectRatio: 420 / 472, columnRange: [66, 75],   speed: 0.20 },
  // R2 — small light green accent
  { type: 'colour', variant: 'round-3', desktopSize: 110, mobileSize: 48,  columnRange: [76, 83],   speed: 0.14, colour: '#a5f1ad' },
  // R3 — large photo blob (bleeds off right edge)
  { type: 'image',  variant: 'blob-2',  desktopSize: 350, mobileSize: 140, aspectRatio: 462 / 380, columnRange: [85, 98],   speed: 0.22 },
  // R4 — small teal accent
  { type: 'colour', variant: 'round-1', desktopSize: 85,  mobileSize: 38,  aspectRatio: 257 / 211, columnRange: [83, 92],   speed: 0.12, colour: '#39b0a3' },
  // R5 — small photo blob (bleeds off right, mirrors L1)
  { type: 'image',  variant: 'blob-3',  desktopSize: 180, mobileSize: 75,  columnRange: [90, 100],  speed: 0.19 },
  // R6 — small mid-green accent
  { type: 'colour', variant: 'round-2', desktopSize: 100, mobileSize: 42,  aspectRatio: 268 / 228, columnRange: [68, 75],   speed: 0.10, colour: '#40aa3b' },
  // R7 — small photo blob (far right)
  { type: 'image',  variant: 'blob-1',  desktopSize: 170, mobileSize: 70,  aspectRatio: 420 / 472, columnRange: [88, 98],   speed: 0.19 },
  // R8 — tiny dark green accent
  { type: 'colour', variant: 'round-2', desktopSize: 60,  mobileSize: 26,  aspectRatio: 268 / 228, columnRange: [76, 84],   speed: 0.11, colour: '#216732' },
]

const IMAGE_SLOT_COUNT = PEBBLE_SLOTS.filter(s => s.type === 'image').length // 8

/** Minimum gap (px) between any two pebbles — prevents visual overlap */
const COLLISION_PAD = 50

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = a[i] as T
    a[i] = a[j] as T
    a[j] = tmp
  }
  return a
}

/** Responsive size using CSS clamp */
function clampSize(mobile: number, desktop: number): string {
  return `clamp(${mobile}px, ${(desktop / 14.4).toFixed(1)}vw, ${desktop}px)`
}

/** Random number in [min, max) */
function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

/* -------------------------------------------------------------------------- */
/*  Collision detection                                                        */
/* -------------------------------------------------------------------------- */

interface PebbleBounds {
  xMin: number // px
  xMax: number // px
  yMin: number // px
  yMax: number // px
}

/** Check whether two axis-aligned bounding boxes overlap (with padding) */
function boundsOverlap(a: PebbleBounds, b: PebbleBounds): boolean {
  return (
    a.xMin - COLLISION_PAD < b.xMax &&
    a.xMax + COLLISION_PAD > b.xMin &&
    a.yMin - COLLISION_PAD < b.yMax &&
    a.yMax + COLLISION_PAD > b.yMin
  )
}

/** Get bounds for a pebble slot at a given position */
function getPebbleBounds(slot: PebbleSlot, xPercent: number, y: number, vw: number): PebbleBounds {
  const width = slot.desktopSize
  const height = slot.desktopSize * (slot.aspectRatio ?? 1)
  const xPx = (xPercent / 100) * vw
  return {
    xMin: xPx,
    xMax: xPx + width,
    yMin: y,
    yMax: y + height,
  }
}

/* -------------------------------------------------------------------------- */
/*  Pebble state                                                               */
/* -------------------------------------------------------------------------- */

interface PebbleState {
  /** Current Y position in px (0 = viewport top) */
  y: number
  /** Fixed X position as % of viewport width */
  xPercent: number
  /** Current image index within pool (image pebbles only) */
  imageIdx: number
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                             */
/* -------------------------------------------------------------------------- */

export function ScrollingPebbleBackground({
  images = [],
  className = '',
}: ScrollingPebbleBackgroundProps) {
  // Shuffle images once on mount, then allocate pools to image slots
  const shuffledUrls = useMemo(() => {
    if (images.length === 0) return []
    return shuffle(images).map(img => img.thumbnail_url || img.image_url)
  }, [images])

  // Distribute images evenly across image slots
  const poolSize = Math.max(1, Math.floor(shuffledUrls.length / IMAGE_SLOT_COUNT))

  // Build image pools for each image slot
  const imagePools = useMemo(() => {
    let imageSlotIdx = 0
    return PEBBLE_SLOTS.map(slot => {
      if (slot.type !== 'image') return []
      const start = imageSlotIdx * poolSize
      imageSlotIdx++
      return shuffledUrls.slice(start, start + poolSize)
    })
  }, [shuffledUrls, poolSize])

  // Mutable state for animation (not React state — avoids re-renders)
  const pebbleStates = useRef<PebbleState[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const pebbleRefs = useRef<(HTMLDivElement | null)[]>([])
  const rafRef = useRef<number>(0)
  const imageIdxRefs = useRef<number[]>(PEBBLE_SLOTS.map(() => 0))

  // Force re-render when image index changes (to update src)
  const [imageVersion, setImageVersion] = useState(0)

  // Initialise pebble positions on mount — stagger Y evenly with jitter,
  // using collision detection to prevent initial overlaps
  const initPebbles = useCallback(() => {
    const vh = window.innerHeight
    const vw = window.innerWidth
    const spacing = (vh * 1.4) / PEBBLE_SLOTS.length
    const states: PebbleState[] = []

    PEBBLE_SLOTS.forEach((slot, i) => {
      let candidateY = -vh * 0.2 + i * spacing + randRange(-spacing * 0.15, spacing * 0.15)
      const xPercent = randRange(slot.columnRange[0], slot.columnRange[1])

      // Check against already-placed pebbles and push down if overlapping
      const candidateBounds = getPebbleBounds(slot, xPercent, candidateY, vw)
      for (const prev of states) {
        const prevSlot = PEBBLE_SLOTS[states.indexOf(prev)]! // eslint-disable-line @typescript-eslint/no-non-null-assertion
        const prevBounds = getPebbleBounds(prevSlot, prev.xPercent, prev.y, vw)
        if (boundsOverlap(candidateBounds, prevBounds)) {
          candidateY = prevBounds.yMax + COLLISION_PAD + randRange(20, 80)
          candidateBounds.yMin = candidateY
          candidateBounds.yMax = candidateY + slot.desktopSize * (slot.aspectRatio ?? 1)
        }
      }

      states.push({ y: candidateY, xPercent, imageIdx: 0 })
    })

    pebbleStates.current = states
    imageIdxRefs.current = PEBBLE_SLOTS.map(() => 0)
  }, [])

  // Animation loop
  useEffect(() => {
    // Guard: skip animation if reduced motion preferred
    const mql = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null
    if (mql?.matches) {
      // Static layout — spread pebbles evenly across viewport
      const vh = window.innerHeight
      pebbleStates.current = PEBBLE_SLOTS.map((slot, i) => ({
        y: (i / PEBBLE_SLOTS.length) * vh * 0.9 + vh * 0.05,
        xPercent: randRange(slot.columnRange[0], slot.columnRange[1]),
        imageIdx: 0,
      }))
      // Apply static positions
      pebbleStates.current.forEach((state, i) => {
        const el = pebbleRefs.current[i]
        if (el) {
          el.style.transform = `translateY(${state.y}px)`
          el.style.left = `${state.xPercent}%`
        }
      })
      return
    }

    initPebbles()

    // Apply initial positions
    pebbleStates.current.forEach((state, i) => {
      const el = pebbleRefs.current[i]
      if (el) {
        el.style.left = `${state.xPercent}%`
      }
    })

    let lastTime = 0
    let needsImageUpdate = false

    const animate = (time: number) => {
      if (!lastTime) lastTime = time
      // Cap delta to avoid huge jumps when tab is backgrounded
      const delta = Math.min(time - lastTime, 100)
      lastTime = time

      // Normalise to 60fps (16.67ms per frame)
      const frameFactor = delta / 16.67

      const vh = window.innerHeight
      const vw = window.innerWidth

      PEBBLE_SLOTS.forEach((slot, i) => {
        const state = pebbleStates.current[i]
        if (!state) return

        // Move upward
        state.y -= slot.speed * frameFactor

        // Compute pebble height for recycle check
        const pebbleHeight = slot.desktopSize * (slot.aspectRatio ?? 1)

        // Recycle: when bottom edge passes above viewport top
        if (state.y + pebbleHeight < -50) {
          let candidateY = vh + randRange(20, 150)

          // Collision check: ensure new position doesn't overlap any visible pebble
          const candidateBounds = getPebbleBounds(slot, state.xPercent, candidateY, vw)
          for (let j = 0; j < PEBBLE_SLOTS.length; j++) {
            if (j === i) continue
            const other = pebbleStates.current[j]
            if (!other) continue
            const otherSlot = PEBBLE_SLOTS[j]! // eslint-disable-line @typescript-eslint/no-non-null-assertion
            const otherBounds = getPebbleBounds(otherSlot, other.xPercent, other.y, vw)
            if (boundsOverlap(candidateBounds, otherBounds)) {
              // Push further below the overlapping pebble
              candidateY = Math.max(candidateY, otherBounds.yMax + COLLISION_PAD + randRange(20, 60))
              candidateBounds.yMin = candidateY
              candidateBounds.yMax = candidateY + pebbleHeight
            }
          }

          state.y = candidateY

          // Advance image on recycle
          if (slot.type === 'image') {
            const pool = imagePools[i]
            if (pool && pool.length > 1) {
              imageIdxRefs.current[i] = ((imageIdxRefs.current[i] ?? 0) + 1) % pool.length
              needsImageUpdate = true
            }
          }
        }

        // Apply transform
        const el = pebbleRefs.current[i]
        if (el) {
          el.style.transform = `translateY(${state.y}px)`
        }
      })

      // Batch image updates to minimise re-renders
      if (needsImageUpdate) {
        needsImageUpdate = false
        setImageVersion(v => v + 1)
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    // Listen for runtime reduced-motion changes
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        cancelAnimationFrame(rafRef.current)
      } else {
        lastTime = 0
        rafRef.current = requestAnimationFrame(animate)
      }
    }
    mql?.addEventListener('change', onChange)

    return () => {
      cancelAnimationFrame(rafRef.current)
      mql?.removeEventListener('change', onChange)
    }
  }, [initPebbles, imagePools])

  // Track which image slot we're on for pool assignment
  // (imageVersion in dependency ensures re-render picks up new indices)
  void imageVersion

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 z-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden="true"
    >
      {PEBBLE_SLOTS.map((slot, i) => {
        const ar = slot.aspectRatio ?? 1
        const width = clampSize(slot.mobileSize, slot.desktopSize)
        const height = clampSize(
          Math.round(slot.mobileSize * ar),
          Math.round(slot.desktopSize * ar),
        )

        if (slot.type === 'colour') {
          return (
            <div
              key={`pebble-${i}`}
              data-testid={`pebble-${i}`}
              ref={el => { pebbleRefs.current[i] = el }}
              className="absolute opacity-80"
              style={{ width, height, willChange: 'transform' }}
            >
              <PebbleShape
                variant={slot.variant}
                bgColor={slot.colour}
                size="w-full h-full"
              />
            </div>
          )
        }

        // Image pebble
        const pool = imagePools[i]
        if (!pool || pool.length === 0) return null

        const currentImageIdx = imageIdxRefs.current[i] ?? 0
        const currentUrl = pool[currentImageIdx % pool.length] as string

        return (
          <div
            key={`pebble-${i}`}
            data-testid={`pebble-${i}`}
            ref={el => { pebbleRefs.current[i] = el }}
            className="absolute opacity-90"
            style={{ width, height, willChange: 'transform' }}
          >
            <PebbleShape
              variant={slot.variant}
              src={currentUrl}
              size="w-full h-full"
            />
          </div>
        )
      })}
    </div>
  )
}
